import type { PostalCodeOption } from '@houzy/schemas/partner-cockpit/company';
import { useCallback, useEffect, useMemo, useState } from 'react';

import type { Canton } from '../types';

export type FilteredCanton = {
    id: string;
    label: string;
    postalCodes: PostalCodeOption[];
};

export type UseWorkRegionTreeReturn = {
    filteredCantonNodes: FilteredCanton[];
    expandedCantons: ReadonlySet<string>;
    toggleExpandCanton: (id: string) => void;
    toggleCantonSelection: (id: string) => void;
    togglePostalCode: (code: string) => void;
    visiblePostalCodes: ReadonlySet<string> | undefined;
};

export function useWorkRegionTree({
    cantons,
    postalCodesByCanton,
    selectedPostalCodes,
    onSelectionChange,
    search,
    rangeFilterCodes,
}: {
    cantons: readonly Canton[];
    postalCodesByCanton: Record<string, PostalCodeOption[]>;
    selectedPostalCodes: ReadonlySet<string>;
    onSelectionChange: (next: ReadonlySet<string>) => void;
    search: string;
    rangeFilterCodes?: ReadonlySet<string>;
}): UseWorkRegionTreeReturn {
    const [expandedCantons, setExpandedCantons] = useState<ReadonlySet<string>>(new Set());

    const cantonNodes = useMemo(
        () =>
            cantons.map((canton) => ({
                id: canton.id,
                label: canton.name,
                postalCodes: postalCodesByCanton[canton.id] ?? [],
            })),
        [cantons, postalCodesByCanton],
    );

    const filteredCantonNodes = useMemo<FilteredCanton[]>(() => {
        let result = cantonNodes;

        if (search) {
            const lower = search.toLowerCase();
            const searchResult: FilteredCanton[] = [];
            for (const canton of result) {
                if (canton.label.toLowerCase().includes(lower)) {
                    searchResult.push(canton);
                    continue;
                }
                const filtered = canton.postalCodes.filter(
                    ({ code, name }) => code.includes(search) || name.toLowerCase().includes(lower),
                );
                if (filtered.length > 0) {
                    searchResult.push({ ...canton, postalCodes: filtered });
                }
            }
            result = searchResult;
        }

        if (rangeFilterCodes) {
            const rangeResult: FilteredCanton[] = [];
            for (const canton of result) {
                const filtered = canton.postalCodes.filter(({ code }) => rangeFilterCodes.has(code));
                if (filtered.length > 0) {
                    rangeResult.push({ ...canton, postalCodes: filtered });
                }
            }
            result = rangeResult;
        }

        return result;
    }, [cantonNodes, search, rangeFilterCodes]);

    useEffect(() => {
        if (search) {
            setExpandedCantons(new Set(filteredCantonNodes.map((c) => c.id)));
        } else {
            setExpandedCantons(new Set());
        }
    }, [search, filteredCantonNodes]);

    const visiblePostalCodes = useMemo<ReadonlySet<string> | undefined>(() => {
        if (!search && !rangeFilterCodes) {
            return undefined;
        }
        const codes = new Set<string>();
        for (const canton of filteredCantonNodes) {
            for (const { code } of canton.postalCodes) {
                codes.add(code);
            }
        }
        return codes;
    }, [search, rangeFilterCodes, filteredCantonNodes]);

    const toggleExpandCanton = useCallback((id: string) => {
        setExpandedCantons((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    }, []);

    const toggleCantonSelection = useCallback(
        (id: string) => {
            const canton = filteredCantonNodes.find((node) => node.id === id);
            if (!canton) {
                return;
            }
            const codes = canton.postalCodes.map(({ code }) => code);
            const next = new Set(selectedPostalCodes);
            const allCantonSelected = codes.every((code) => next.has(code));
            if (allCantonSelected) {
                for (const code of codes) {
                    next.delete(code);
                }
            } else {
                for (const code of codes) {
                    next.add(code);
                }
            }
            onSelectionChange(next);
        },
        [filteredCantonNodes, selectedPostalCodes, onSelectionChange],
    );

    const togglePostalCode = useCallback(
        (code: string) => {
            const next = new Set(selectedPostalCodes);
            if (next.has(code)) {
                next.delete(code);
            } else {
                next.add(code);
            }
            onSelectionChange(next);
        },
        [selectedPostalCodes, onSelectionChange],
    );

    return {
        filteredCantonNodes,
        expandedCantons,
        toggleExpandCanton,
        toggleCantonSelection,
        togglePostalCode,
        visiblePostalCodes,
    };
}
